import { Injectable, ComponentRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import {
	BehaviorSubject,
	fromEvent,
	merge,
	Observable,
	Observer,
	Subscription
} from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IMapBounds } from '../view-shared/map-base/map-base.models';
import {
	IGlobalStateStore,
	IGlobalUserStore,
	IDateRange
} from './global.models';
import { Constants } from './constants';
import { FormControl } from '@angular/forms';
import { UserActiveService } from './user-active.service';

import englishLocale from 'node_modules/moment/locale/en-gb';
import danishLocale from 'node_modules/moment/locale/da';
import norwegianLocale from 'node_modules/moment/locale/nb';
import swedishLocale from 'node_modules/moment/locale/sv';
import spanishLocale from 'node_modules/moment/locale/es';

import * as Highcharts from 'highcharts';
import * as moment from 'moment';
import * as mTZ from 'moment-timezone';
import { BreakpointObserver } from '@angular/cdk/layout';
import { DialogToastComponent } from '../view-shared/dialogs/dialog-toast/dialog-toast.component';
import { ToastService } from '../view-shared/dialogs/toast.service';
import { MatTabChangeEvent } from '@angular/material/tabs';
(window as any).moment = moment;
mTZ();

@Injectable({
	providedIn: 'root'
})
export class GlobalService {
	private layoutChanges: Subscription = new Subscription();

	constructor(
		private translate: TranslateService,
		private userService: UserActiveService,
		private http: HttpClient,
		private toastService: ToastService,
		private breakPointObserver: BreakpointObserver
	) {
		// if lover version found in localstorage force eject user to login
		const currentVersion = Constants.version;
		const registreredVersion =
			Number(localStorage.getItem('version')) || null;
		if (registreredVersion && registreredVersion < currentVersion) {
			this.clearUserSettings();
			localStorage.clear();
			localStorage.setItem('version', currentVersion.toString());
			this.userService.deAuthUser();
		}

		if (window.location.href.includes('public')) {
			this.stateStore.public = true;
			this.stateStore.mainMenu = false;
		}

		let didLooseConnection: ComponentRef<DialogToastComponent> = null;

		this.monitorOnline().subscribe(isOnline => {
			if (!isOnline) {
				didLooseConnection = this.toastService.showToast(
					this.translate.instant('ROOT.ERRORS.HTTP.CONNECTION_LOST'),
					'error'
				);
			} else if (didLooseConnection) {
				this.toastService.closeToast(didLooseConnection);
				didLooseConnection = null;

				this.toastService.showToast(
					this.translate.instant(
						'ROOT.ERRORS.HTTP.CONNECTION_RESUMED'
					),
					'success'
				);
			}
		});

		this.layoutChanges = this.breakPointObserver
			.observe(['(min-width:1760px)', '(min-width:1530px)'])
			.subscribe(val => {
				const breaks = Object.values(val.breakpoints).reverse();
				const width: number = breaks[1]
					? 1760
					: breaks[0]
					? 1530
					: 1529;
				this.stateStore.$screenWidth.next(width);
			});
	}

	private monitorOnline() {
		return merge<boolean>(
			fromEvent(window, 'offline').pipe(map(() => false)),
			fromEvent(window, 'online').pipe(map(() => true)),
			new Observable((sub: Observer<boolean>) => {
				sub.next(navigator.onLine);
				sub.complete();
			})
		);
	}

	stateStore: IGlobalStateStore = {
		lensLogoSource: this.getLensLogoSource(),
		drypLogoSource: this.getDrypLogoSource(),
		appTitle: this.getAppTitle(),
		backdrop: new BehaviorSubject(false),
		$dateRange: new BehaviorSubject(this.getDateRange()),
		$dateTime: new BehaviorSubject<number>(this.getDateTime()),
		$dateLive: new BehaviorSubject(false),
		$dateReload: new BehaviorSubject(false),
		$screenWidth: new BehaviorSubject(0),
		$plotMouseTrackingEnabled: new BehaviorSubject(true),
		didLogOut: false,
		mapBoundsDefault: null,
		mapBoundsCurrent: new BehaviorSubject<IMapBounds>(null),
		//
		mainMenu: true,
		public: false,
		showFilters: true,
		batchInitDateTimeUtcMilliseconds: 0
	};

	userStore: IGlobalUserStore = {
		$appMenu: new BehaviorSubject('out'),
		$appRoute: new BehaviorSubject('data-inspector/viewer'),
		$appLanguage: new BehaviorSubject('en'),
		$appTimezone: new BehaviorSubject('Europe/Copenhagen'),
		//
		filterPointGroups: new FormControl([]),
		filterByMediaTypes: new FormControl(['media', 'files', 'text']),
		filterActive: new FormControl(true),
		filterDeviceParams: new FormControl(false),
		$filterSensors: new BehaviorSubject(null),
		//
		$chartAutoScale: new BehaviorSubject(true),
		$chartGrouping: new BehaviorSubject('split'),
		$chartShowFlags: new BehaviorSubject(true),
		$chartShowFlagsSystem: new BehaviorSubject(false),
		$showDefaultAnnotations: new BehaviorSubject(true),
		$chartShowThresholds: new BehaviorSubject(true),
		$chartDataType: new BehaviorSubject('adjusted'),
		$chartUnitsMerge: new BehaviorSubject(false),
		$chartHeight: new BehaviorSubject('medium'),
		$chartYaxis: new BehaviorSubject(true),
		//
		$mapTooltip: new BehaviorSubject(false),
		$mapBase: new BehaviorSubject('Basic map'),
		$mapOverlays: new BehaviorSubject([]),
		$mapSliderRange: new BehaviorSubject(0),
		$mapTabDefault: new BehaviorSubject(true),
		radarOpacityToggle: new FormControl(true),
		radarAccumulationMinuteSelected: new FormControl(1),
		//
		$parameterScale: new BehaviorSubject([])
	};

	pageAnimationDone: BehaviorSubject<boolean> = new BehaviorSubject(false);

	subToUserSettings(): void {
		// subscribe to changes in userSettings
		Object.keys(this.userStore).map((key: string) => {
			if (this.userStore[key] !== undefined) {
				if (key.includes('$')) {
					this.userStore[key].subscribe((data: any) => {
						if (!this.userService.currentUserValue) {
							return;
						}
						if (key === '$appLanguage' && data.includes('-')) {
							data = data.split('-');
							data = data[0];
						}
						this.saveUserSettings();
					});
				} else {
					this.userStore[key].valueChanges.subscribe((data: any) => {
						if (!this.userService.currentUserValue) {
							return;
						}
						this.saveUserSettings();
					});
				}
			}
		});
	}

	private clearUserSettings(): void {
		if (this.stateStore.public) {
			return;
		}

		this.http
			.put<any>(`${Constants.API}/api/User/settings`, {
				definition: '{}'
			})
			.subscribe();
	}

	private saveUserSettings(): void {
		let payload: object | string = {};
		for (const [key, val] of Object.entries(this.userStore)) {
			payload[key] = val.value;
		}
		payload = JSON.stringify(payload);
		const uid: string = this.userService.currentUserValue.IdUser;
		const compare: string = localStorage.getItem(`${uid}-user-settings`);

		if (compare && compare === payload) {
			return;
		}

		localStorage.setItem(`${uid}-user-settings`, payload);

		if (this.stateStore.public) {
			return;
		}
		this.http
			.put<any>(`${Constants.API}/api/User/settings`, {
				definition: payload
			})
			.subscribe();
	}

	setLanguage(locale: string = 'en'): void {
		// console.log('setLanguage', locale);
		locale = ['en', 'da', 'nb', 'sv', 'es'].includes(locale)
			? locale
			: 'en';
		switch (locale) {
			case 'da':
				moment.updateLocale('da', danishLocale);
				break;
			case 'nb':
				moment.updateLocale('nb', norwegianLocale);
				break;
			case 'sv':
				moment.updateLocale('sv', swedishLocale);
				break;
			case 'es':
				moment.updateLocale('es', spanishLocale);
				break;
			default:
				moment.updateLocale('en-gb', englishLocale);
				break;
		}

		Highcharts.setOptions({
			lang: {
				months: moment.months(),
				weekdays: moment.weekdays(),
				shortMonths: moment.monthsShort()
			}
		});
		localStorage['language'] = locale;
		this.userStore.$appLanguage.next(locale);
	}

	setTimezone(zone: string = 'Europe/Copenhagen'): void {
		moment.tz.setDefault(zone);
		Highcharts.setOptions({
			time: {
				timezone: zone
			}
		});

		setTimeout(() => {
			this.userStore.$appTimezone.next(zone);
		}, 0);
	}

	/**
	 * Changes the global daterange while preserving the current range in days.
	 * Were jumping backwards if jumpTo is lower than current daterange.begin.
	 * @param jumpTo number in utcmilliseconds
	 */
	jumpToDate(jumpTo: number): void {
		const backwards: boolean =
			jumpTo < this.stateStore.$dateRange.value.begin;
		const rangeDays: number = moment(
			this.stateStore.$dateRange.value.end
		).diff(moment(this.stateStore.$dateRange.value.begin), 'days');
		const begin: number = backwards
			? moment
					.utc(jumpTo)
					.subtract(rangeDays, 'days')
					.startOf('day')
					.valueOf()
			: moment
					.utc(jumpTo)
					.startOf('day')
					.valueOf();
		const end: number = backwards
			? moment
					.utc(jumpTo)
					.endOf('day')
					.valueOf()
			: moment
					.utc(jumpTo)
					.add(rangeDays, 'days')
					.endOf('day')
					.valueOf();
		setTimeout(() => {
			this.stateStore.$dateRange.next({ begin, end });
		}, 0);
	}

	private getDateRange(): IDateRange {
		const dateObject2: IDateRange = {
			begin: moment
				.utc()
				.endOf('day')
				.subtract(2, 'days')
				.startOf('day')
				.valueOf(),
			end: moment
				.utc()
				.endOf('day')
				.valueOf()
		};

		if (
			dateObject2.end >
			moment
				.utc()
				.endOf('day')
				.valueOf()
		) {
			dateObject2.end = moment.utc().valueOf();
		}
		return dateObject2;
	}

	private getDateTime(): number {
		return moment
			.utc()
			.endOf('day')
			.subtract(2, 'days')
			.startOf('day')
			.valueOf();
	}

	getLensLogoSource(forPrint = false): string {
		let lensLogoSource: string = forPrint
			? 'assets/dryp-lens-logo-white-color-reversed.svg'
			: 'assets/dryp-lens-logo-white-white-reversed.svg';

		if (/vcs/.test(document.location.host)) {
			lensLogoSource = 'assets/logo.vcs.svg';
		}
		return lensLogoSource;
	}

	getDrypLogoSource(forPrint = false): string {
		let drypLogoSource: string = forPrint
			? 'assets/logo.dryp-blue.svg'
			: 'assets/logo.dryp-white.svg';
		return drypLogoSource;
	}

	/**
	 * Stores wether or not the user prefers map-view.
	 * @param event The tab-changed event
	 */
	tabChanged(event: MatTabChangeEvent, mapIndex = 0): void {
		window.dispatchEvent(new Event('resize'));
		if (mapIndex !== null)
			this.userStore.$mapTabDefault.next(event.index === mapIndex);
	}

	private getAppTitle(): string {
		let title: string =
			environment.devmode === true
				? environment.endpoint?.split('//')[1]
				: 'Dryp';

		if (/vcs/.test(document.location.host)) {
			title = 'VCS';
		}

		if (/localhost/.test(environment.endpoint)) {
			document.body.style.backgroundColor = 'red';
		} else if (environment.devmode === true) {
			document.body.style.backgroundColor = 'darkorange';
		}

		return title;
	}
}
